/**
 * Interactive editing state machine. The UI feeds it pointer and key events in sheet
 * coordinates; it produces transactions on the History and an overlay to draw.
 * No DOM access: fully unit-testable.
 */
import {
  applyTransaction,
  newId,
  nextTag,
  type Element,
  type History,
  type Id,
  type Op,
  type Point,
  type Project,
  type Rotation,
  type Wire,
  type WireEnd,
} from '@elec/domain';
import { Scene, wirePolyline, type Bounds } from '@elec/renderer';
import { elementBounds, getSymbol, portDirection, portPosition } from '@elec/symbols';
import {
  clipboardCenter,
  copySelection,
  deleteOps,
  mirrorOps,
  moveOps,
  pasteOps,
  rotateOps,
  type ClipboardData,
} from './operations.ts';
import { macroSymbolOps, type Macro } from './macros.ts';
import { projectOnSegment, route, segmentDir, simplify, snap, snapPoint, type Dir } from './routing.ts';

export type Tool =
  | { kind: 'select' }
  /** `preset` places a known point (e.g. "A3" from the single-line diagram) once, then returns to select. */
  | { kind: 'place'; symbolId: string; preset?: { tag: string; props: Element['props'] } }
  | { kind: 'wire' }
  /** Drag a rectangle to create a room on a position plan. */
  | { kind: 'room' }
  /** Click two points of known distance on a plan background to set its scale. */
  | { kind: 'calibrate' };

export interface Calibration {
  a: Point;
  b: Point;
}

export interface Mods {
  shift?: boolean;
  alt?: boolean;
  mod?: boolean;
}

export interface Overlay {
  /** Element being placed (not yet in the project). */
  ghost?: Element;
  /** Wire being drawn. */
  wire?: Point[];
  /** Rubber-band selection; `crossing` when dragged right-to-left. */
  box?: Bounds & { crossing: boolean };
  /** Snap target under the cursor (port, wire or grid point) while wiring. */
  snapPoint?: Point;
  snapKind?: 'port' | 'wire' | 'grid';
  /** Ports of the element under the cursor, while wiring. */
  ports?: Point[];
  /** Room being drawn. */
  room?: Bounds;
  /** Calibration line (first point fixed, second following the cursor). */
  measure?: [Point, Point];
}

/** Human-readable labels for undo entries; the app passes translated strings. */
export interface EditorLabels {
  move: string;
  add: (name: string) => string;
  wire: string;
  delete: string;
  rotate: string;
  mirror: string;
  paste: string;
  edit: string;
  roomName: string;
}

const DEFAULT_LABELS: EditorLabels = {
  move: 'Move',
  add: (n) => `Add ${n}`,
  wire: 'Wire',
  delete: 'Delete',
  rotate: 'Rotate',
  mirror: 'Mirror',
  paste: 'Paste',
  edit: 'Edit',
  roomName: 'Room',
};

type Target = { end: WireEnd; point: Point; dir: Dir | null; kind: 'port' | 'wire' | 'grid' };

type Gesture =
  | { kind: 'none' }
  | { kind: 'move'; start: Point; ids: Id[]; dx: number; dy: number; moved: boolean }
  | { kind: 'box'; start: Point; current: Point; additive: boolean; base: Set<Id> }
  | { kind: 'room'; start: Point; current: Point };

interface WireDraft {
  start: Target;
  /** Points committed so far after the start (corners and clicks). */
  via: Point[];
  /** Direction the wire is heading out of the last committed point. */
  heading: Dir | null;
}

export class Editor {
  readonly history: History;
  private _sheetId: Id;
  private _tool: Tool = { kind: 'select' };
  private _selection = new Set<Id>();
  private gesture: Gesture = { kind: 'none' };
  private wire: WireDraft | null = null;
  private cursor: Point = { x: 0, y: 0 };
  private hoverTarget: Target | null = null;
  private placeRotation: Rotation = 0;
  private measureStart: Point | null = null;
  /** Set when the calibration tool has two points; the app asks for the real distance. */
  pendingCalibration: Calibration | null = null;
  private clipboard: ClipboardData | null = null;
  private pasteCount = 0;
  private listeners = new Set<() => void>();
  private scene: Scene;
  labels: EditorLabels = DEFAULT_LABELS;
  /** Hit tolerance in sheet units; the UI sets it from the zoom level. */
  tolerance = 1.5;
  private version = 0;
  /**
   * Changes shown on the canvas without being applied (the assistant's proposal under the
   * pointer). Valid only for the project it was computed on; `returnTo` is the sheet to go back to.
   */
  private _preview: { project: Project; base: Project; returnTo: Id } | null = null;

  constructor(history: History, sheetId: Id) {
    this.history = history;
    this._sheetId = sheetId;
    this.scene = new Scene(sheetId);
    history.subscribe(() => {
      // Drop selection entries that no longer exist (undo, delete).
      const p = history.project;
      for (const id of this._selection) if (!p.elements[id] && !p.wires[id]) this._selection.delete(id);
      // A preview computed on the old project no longer applies.
      if (this._preview) {
        if (p.sheets[this._preview.returnTo]) this._sheetId = this._preview.returnTo;
        this._preview = null;
      }
      if (!p.sheets[this._sheetId]) this._sheetId = Object.keys(p.sheets)[0]!;
      this.emit();
    });
  }

  // ---------- state ----------

  get sheetId(): Id {
    return this._sheetId;
  }
  get tool(): Tool {
    return this._tool;
  }
  get selection(): ReadonlySet<Id> {
    return this._selection;
  }
  get project(): Project {
    return this.history.project;
  }
  /** Changes on every state change; handy for React's useSyncExternalStore. */
  get stateVersion(): number {
    return this.version;
  }
  get hasClipboard(): boolean {
    return !!this.clipboard;
  }
  get isWiring(): boolean {
    return !!this.wire;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  private sceneNow(): Scene {
    if (this.scene.sheetId !== this._sheetId) this.scene = new Scene(this._sheetId);
    this.scene.sync(this.history.project);
    return this.scene;
  }

  setSheet(id: Id): void {
    if (id === this._sheetId || !this.project.sheets[id]) return;
    this._sheetId = id;
    this._selection.clear();
    this.cancel();
    this.emit();
  }

  setTool(tool: Tool): void {
    this.wire = null;
    this.gesture = { kind: 'none' };
    this.hoverTarget = null;
    this.measureStart = null;
    this._tool = tool;
    if (tool.kind === 'place') this.placeRotation = 0;
    this.emit();
  }

  select(ids: Iterable<Id>): void {
    this._selection = new Set(ids);
    this.emit();
  }

  selectAll(): void {
    const p = this.project;
    this.select([
      ...Object.values(p.elements)
        .filter((e) => e.sheetId === this._sheetId)
        .map((e) => e.id),
      ...Object.values(p.wires)
        .filter((w) => w.sheetId === this._sheetId)
        .map((w) => w.id),
    ]);
  }

  /** Esc: cancel the wire in progress, else leave the current tool, else clear the selection. */
  cancel(): void {
    if (this.wire) this.wire = null;
    else if (this.measureStart) this.measureStart = null;
    else if (this._tool.kind !== 'select') this._tool = { kind: 'select' };
    else this._selection.clear();
    this.gesture = { kind: 'none' };
    this.emit();
  }

  /**
   * Show `ops` on the canvas without applying them (null: stop). The view moves to `sheetId` when
   * given (it may be a sheet the ops create) and comes back when the preview stops.
   */
  setPreview(ops: Op[] | null, sheetId?: Id): void {
    if (!ops || !ops.length) {
      if (!this._preview) return;
      const back = this._preview.returnTo;
      this._preview = null;
      if (this.project.sheets[back]) this._sheetId = back;
      this.emit();
      return;
    }
    const base = this.project;
    const project = applyTransaction(base, { label: '', ops }).project;
    const returnTo = this._preview?.returnTo ?? this._sheetId;
    this._preview = { project, base, returnTo };
    this._sheetId = sheetId && project.sheets[sheetId] ? sheetId : returnTo;
    this.emit();
  }

  /** The previewed project, if a preview is on and still matches the project. */
  get preview(): Project | null {
    return this._preview && this._preview.base === this.project ? this._preview.project : null;
  }

  /** The project as it should be displayed, including an in-progress drag or a preview. */
  displayProject(): Project {
    const preview = this.preview;
    if (preview) return preview;
    const g = this.gesture;
    if (g.kind !== 'move' || (!g.dx && !g.dy)) return this.project;
    return applyTransaction(this.project, { label: '', ops: moveOps(this.project, g.ids, g.dx, g.dy) })
      .project;
  }

  overlay(): Overlay {
    const o: Overlay = {};
    const g = this.gesture;
    if (g.kind === 'box') {
      o.box = {
        minX: Math.min(g.start.x, g.current.x),
        minY: Math.min(g.start.y, g.current.y),
        maxX: Math.max(g.start.x, g.current.x),
        maxY: Math.max(g.start.y, g.current.y),
        crossing: g.current.x < g.start.x,
      };
    }
    if (this._tool.kind === 'place') o.ghost = this.ghostElement(this._tool.symbolId);
    if (g.kind === 'room') o.room = rectOf(snapPoint(g.start), snapPoint(g.current));
    if (this._tool.kind === 'calibrate' && this.measureStart) o.measure = [this.measureStart, this.cursor];
    if (this._tool.kind === 'wire') {
      const t = this.hoverTarget;
      if (t) {
        o.snapPoint = t.point;
        o.snapKind = t.kind;
      }
      o.ports = this.portsNear(this.cursor);
      if (this.wire) o.wire = this.wirePreview(t);
    }
    return o;
  }

  // ---------- pointer input (sheet coordinates) ----------

  pointerDown(p: Point, mods: Mods = {}): void {
    this.cursor = p;
    switch (this._tool.kind) {
      case 'place':
        return this.placeAt(p);
      case 'wire':
        return this.wireClick(p);
      case 'room':
        this.gesture = { kind: 'room', start: p, current: p };
        this.emit();
        return;
      case 'calibrate':
        if (!this.measureStart) this.measureStart = p;
        else {
          this.pendingCalibration = { a: this.measureStart, b: p };
          this.measureStart = null;
          this._tool = { kind: 'select' };
        }
        this.emit();
        return;
      case 'select': {
        const hit = this.sceneNow().hitTest(p, this.tolerance);
        if (!hit) {
          this.gesture = {
            kind: 'box',
            start: p,
            current: p,
            additive: !!mods.shift,
            base: new Set(this._selection),
          };
          if (!mods.shift) this._selection.clear();
          this.emit();
          return;
        }
        if (mods.shift) {
          if (this._selection.has(hit.id)) this._selection.delete(hit.id);
          else this._selection.add(hit.id);
        } else if (!this._selection.has(hit.id)) this._selection = new Set([hit.id]);
        this.gesture = { kind: 'move', start: p, ids: [...this._selection], dx: 0, dy: 0, moved: false };
        this.emit();
      }
    }
  }

  pointerMove(p: Point): void {
    this.cursor = p;
    const g = this.gesture;
    if (g.kind === 'move') {
      g.dx = snap(p.x - g.start.x);
      g.dy = snap(p.y - g.start.y);
      g.moved ||= g.dx !== 0 || g.dy !== 0;
    } else if (g.kind === 'box' || g.kind === 'room') {
      g.current = p;
    } else if (this._tool.kind === 'wire') {
      this.hoverTarget = this.findTarget(p, this.wire?.start);
    }
    this.emit();
  }

  pointerUp(p: Point): void {
    this.cursor = p;
    const g = this.gesture;
    this.gesture = { kind: 'none' };
    if (g.kind === 'move' && g.moved) {
      this.history.dispatch({ label: this.labels.move, ops: moveOps(this.project, g.ids, g.dx, g.dy) });
    } else if (g.kind === 'room') {
      const r = rectOf(snapPoint(g.start), snapPoint(p));
      if (r.maxX - r.minX >= 5 && r.maxY - r.minY >= 5) {
        const el: Element = {
          id: newId(),
          sheetId: this._sheetId,
          symbolId: 'plan.room',
          x: r.minX,
          y: r.minY,
          rotation: 0,
          mirrored: false,
          tag: '',
          props: { name: this.labels.roomName, roomType: 'other', w: r.maxX - r.minX, h: r.maxY - r.minY },
        };
        this.history.dispatch({
          label: this.labels.add(this.labels.roomName),
          ops: [{ op: 'add', coll: 'elements', value: el }],
        });
        this._selection = new Set([el.id]);
      }
    } else if (g.kind === 'box') {
      const box = this.overlayBox(g);
      if (box.maxX - box.minX > 0.5 || box.maxY - box.minY > 0.5) {
        const picked = this.itemsInBox(box, box.crossing);
        this._selection = g.additive ? new Set([...g.base, ...picked]) : new Set(picked);
      }
    }
    this.emit();
  }

  /** Double-click while wiring finishes the wire at the cursor. */
  doubleClick(p: Point): void {
    if (this._tool.kind === 'wire' && this.wire) {
      const t = this.findTarget(p, this.wire.start);
      this.finishWire(t);
    }
  }

  // ---------- keyboard-level commands ----------

  deleteSelection(): void {
    if (!this._selection.size) return;
    this.history.dispatch({ label: this.labels.delete, ops: deleteOps(this.project, this._selection) });
    this._selection.clear();
    this.emit();
  }

  rotate(): void {
    if (this._tool.kind === 'place') {
      this.placeRotation = ((this.placeRotation + 90) % 360) as Rotation;
      this.emit();
      return;
    }
    if (!this._selection.size) return;
    this.history.dispatch({ label: this.labels.rotate, ops: rotateOps(this.project, this._selection) });
  }

  mirror(): void {
    if (!this._selection.size) return;
    this.history.dispatch({ label: this.labels.mirror, ops: mirrorOps(this.project, this._selection) });
  }

  nudge(dx: number, dy: number): void {
    if (!this._selection.size) return;
    this.history.dispatch({ label: this.labels.move, ops: moveOps(this.project, this._selection, dx, dy) });
  }

  /** Remove the last corner of the wire in progress (Backspace). */
  undoWirePoint(): void {
    if (!this.wire) return;
    if (this.wire.via.length) {
      this.wire.via.pop();
      const last = this.wire.via.at(-1) ?? this.wire.start.point;
      const prev = this.wire.via.at(-2) ?? (this.wire.via.length ? this.wire.start.point : null);
      this.wire.heading = prev ? segmentDir(prev, last) : this.wire.start.dir;
    } else this.wire = null;
    this.emit();
  }

  copy(): ClipboardData | null {
    if (!this._selection.size) return null;
    this.clipboard = copySelection(this.project, this._selection);
    this.pasteCount = 0;
    this.emit();
    return this.clipboard;
  }

  cut(): ClipboardData | null {
    const data = this.copy();
    if (data) this.deleteSelection();
    return data;
  }

  /** Paste the clipboard (or `data`) at `at` if given, else offset from the original. */
  paste(data: ClipboardData | null = this.clipboard, at?: Point): void {
    if (!data || (!data.elements.length && !data.wires.length)) return;
    if (data !== this.clipboard) {
      this.clipboard = data;
      this.pasteCount = 0;
    }
    let dx: number;
    let dy: number;
    if (at) {
      const c = clipboardCenter(data);
      dx = snap(at.x - c.x);
      dy = snap(at.y - c.y);
    } else {
      this.pasteCount++;
      dx = dy = 10 * this.pasteCount;
    }
    const { ops, ids } = pasteOps(this.project, data, this._sheetId, dx, dy);
    this.history.dispatch({ label: this.labels.paste, ops });
    this._selection = new Set(ids);
    this.emit();
  }

  duplicate(): void {
    const data = this.copy();
    if (data) this.paste(data);
  }

  /** Insert a macro (centred on `at`), with the user symbols it needs, as one undo step. */
  insertMacro(macro: Macro, at?: Point): void {
    const data = macro.data;
    if (!data.elements.length && !data.wires.length) return;
    const symOps = macroSymbolOps(this.project, macro);
    const base = symOps.length
      ? applyTransaction(this.project, { label: '', ops: symOps }).project
      : this.project;
    const c = clipboardCenter(data);
    const dx = at ? snap(at.x - c.x) : 0;
    const dy = at ? snap(at.y - c.y) : 0;
    const { ops, ids } = pasteOps(base, data, this._sheetId, dx, dy);
    this.history.dispatch({ label: this.labels.paste, ops: [...symOps, ...ops] });
    this._selection = new Set(ids);
    this.emit();
  }

  /** Apply property/field edits from the inspector as one undoable step. */
  update(ops: Op[]): void {
    this.history.dispatch({ label: this.labels.edit, ops });
  }

  // ---------- placing ----------

  private ghostElement(symbolId: string): Element | undefined {
    const def = getSymbol(symbolId);
    if (!def) return undefined;
    const props: Element['props'] = {};
    for (const f of def.fields ?? []) if ('default' in f && f.default !== undefined) props[f.key] = f.default;
    const preset = this._tool.kind === 'place' ? this._tool.preset : undefined;
    if (preset) Object.assign(props, preset.props);
    return {
      id: 'ghost',
      sheetId: this._sheetId,
      symbolId,
      x: snap(this.cursor.x - def.width / 2),
      y: snap(this.cursor.y - def.height / 2),
      rotation: this.placeRotation,
      mirrored: false,
      tag: preset ? preset.tag : def.tagPrefix ? nextTag(this.project, def.tagPrefix) : '',
      props,
    };
  }

  private placeAt(p: Point): void {
    if (this._tool.kind !== 'place') return;
    this.cursor = p;
    const ghost = this.ghostElement(this._tool.symbolId);
    const def = getSymbol(this._tool.symbolId);
    if (!ghost || !def) return;
    const el: Element = { ...ghost, id: newId() };
    this.history.dispatch({
      label: this.labels.add(def.name[this.project.meta.locale]),
      ops: [{ op: 'add', coll: 'elements', value: el }],
    });
    this._selection = new Set([el.id]);
    if (this._tool.preset) this._tool = { kind: 'select' };
    this.emit();
  }

  /** Cancel a pending calibration (the dialog was closed). */
  clearCalibration(): void {
    this.pendingCalibration = null;
    this.emit();
  }

  // ---------- wiring ----------

  private portsNear(p: Point): Point[] {
    const project = this.project;
    const out: Point[] = [];
    const r = this.tolerance * 4;
    for (const it of this.sceneNow().query({ minX: p.x - r, minY: p.y - r, maxX: p.x + r, maxY: p.y + r })) {
      if (it.kind !== 'element') continue;
      const el = project.elements[it.id];
      const def = el && getSymbol(el.symbolId);
      if (!el || !def) continue;
      for (const port of def.ports) out.push(portPosition(def, el, port));
    }
    return out;
  }

  /** What a click at p would connect to: nearest port, else a point on a wire, else the grid. */
  private findTarget(p: Point, exclude?: Target): Target {
    const project = this.project;
    const tol = this.tolerance * 1.5;
    let best: Target | null = null;
    let bestD = tol;
    const scene = this.sceneNow();
    for (const it of scene.query({
      minX: p.x - tol * 3,
      minY: p.y - tol * 3,
      maxX: p.x + tol * 3,
      maxY: p.y + tol * 3,
    })) {
      if (it.kind !== 'element') continue;
      const el = project.elements[it.id];
      const def = el && getSymbol(el.symbolId);
      if (!el || !def) continue;
      for (const port of def.ports) {
        const pp = portPosition(def, el, port);
        const d = Math.hypot(pp.x - p.x, pp.y - p.y);
        if (d <= bestD) {
          bestD = d;
          best = {
            end: { kind: 'port', elementId: el.id, portId: port.id },
            point: pp,
            dir: portDirection(port, el),
            kind: 'port',
          };
        }
      }
    }
    if (best) return best;
    const hit = scene.hitTest(p, this.tolerance);
    if (hit?.kind === 'wire') {
      const w = project.wires[hit.id];
      const pts = w && wirePolyline(project, w);
      if (pts) {
        let proj: ReturnType<typeof projectOnSegment> | null = null;
        let seg: [Point, Point] | null = null;
        for (let i = 0; i < pts.length - 1; i++) {
          const r = projectOnSegment(p, pts[i]!, pts[i + 1]!);
          if (!proj || r.dist < proj.dist) {
            proj = r;
            seg = [pts[i]!, pts[i + 1]!];
          }
        }
        if (proj && seg) {
          // Snap along the segment so T-junctions land on the grid.
          const horizontal = Math.abs(seg[0].y - seg[1].y) < 1e-6;
          const pt = horizontal
            ? { x: snap(proj.point.x), y: seg[0].y }
            : { x: seg[0].x, y: snap(proj.point.y) };
          const notSelf =
            !exclude ||
            exclude.kind !== 'wire' ||
            Math.hypot(exclude.point.x - pt.x, exclude.point.y - pt.y) > 0.01;
          if (notSelf) return { end: { kind: 'point', ...pt }, point: pt, dir: null, kind: 'wire' };
        }
      }
    }
    const g = snapPoint(p);
    return { end: { kind: 'point', ...g }, point: g, dir: null, kind: 'grid' };
  }

  private lastPoint(): Point {
    return this.wire!.via.at(-1) ?? this.wire!.start.point;
  }

  private wirePreview(target: Target | null): Point[] {
    const w = this.wire!;
    const last = this.lastPoint();
    const t = target?.point ?? snapPoint(this.cursor);
    const arrive = target?.kind === 'port' && target.dir ? target.dir : null;
    return simplify([w.start.point, ...w.via, ...route(last, w.heading, t, arrive), t]);
  }

  private wireClick(p: Point): void {
    const t = this.findTarget(p, this.wire?.start);
    if (!this.wire) {
      this.wire = { start: t, via: [], heading: t.dir };
      this.hoverTarget = t;
      this.emit();
      return;
    }
    const last = this.lastPoint();
    const same = Math.hypot(t.point.x - last.x, t.point.y - last.y) < 0.01;
    if (t.kind === 'port' || t.kind === 'wire') {
      if (!same || this.wire.via.length) return this.finishWire(t);
      return;
    }
    if (same) return; // ignore repeated clicks on the same grid point
    // Free click: commit this leg (with its corners) and keep drawing.
    const corners = route(last, this.wire.heading, t.point, null);
    const pts = [...corners, t.point];
    this.wire.via.push(...pts);
    const prev = pts.length >= 2 ? pts.at(-2)! : last;
    this.wire.heading = segmentDir(prev, t.point);
    this.emit();
  }

  private finishWire(t: Target): void {
    const w = this.wire;
    if (!w) return;
    const last = this.lastPoint();
    const arrive = t.kind === 'port' ? t.dir : null;
    const all = simplify([w.start.point, ...w.via, ...route(last, w.heading, t.point, arrive), t.point]);
    this.wire = null;
    if (all.length < 2) {
      this.emit();
      return;
    }
    const wire: Wire = {
      id: newId(),
      sheetId: this._sheetId,
      from: w.start.end,
      to: t.end,
      via: all.slice(1, -1),
    };
    this.history.dispatch({ label: this.labels.wire, ops: [{ op: 'add', coll: 'wires', value: wire }] });
    // Ending on a free grid point (double-click) or on a port: ready for the next wire.
    this.emit();
  }

  // ---------- box selection ----------

  private overlayBox(g: Extract<Gesture, { kind: 'box' }>) {
    return {
      minX: Math.min(g.start.x, g.current.x),
      minY: Math.min(g.start.y, g.current.y),
      maxX: Math.max(g.start.x, g.current.x),
      maxY: Math.max(g.start.y, g.current.y),
      crossing: g.current.x < g.start.x,
    };
  }

  /** Window (left→right): items fully inside. Crossing (right→left): items touching. */
  private itemsInBox(b: Bounds, crossing: boolean): Id[] {
    const out: Id[] = [];
    const inside = (x: number, y: number) => x >= b.minX && x <= b.maxX && y >= b.minY && y <= b.maxY;
    const project = this.project;
    for (const it of this.sceneNow().query(b)) {
      if (it.kind === 'element') {
        const el = project.elements[it.id];
        const def = el && getSymbol(el.symbolId);
        if (!el || !def) continue;
        const eb = elementBounds(def, el);
        if (crossing || (inside(eb.minX, eb.minY) && inside(eb.maxX, eb.maxY))) out.push(it.id);
      } else if (it.points) {
        if (crossing || it.points.every((q) => inside(q.x, q.y))) out.push(it.id);
      }
    }
    return out;
  }
}

function rectOf(a: Point, b: Point): Bounds {
  return {
    minX: Math.min(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x),
    maxY: Math.max(a.y, b.y),
  };
}
